/**
 * 十六張標準牌型向聽數：五面子一眼，胡牌 -1、聽牌 0。
 *
 * 面子、搭子、對子都不會跨花色，所以萬／筒／條／字四組各自拆解，再合併各組的
 * （面子數, 搭子數, 是否有眼）組合求最小值。各組拆解結果以 9 張計數為 key 長期快取，
 * 同一組牌形在整局、跨決策都只拆一次；
 * 合併時同一個（面子數, 是否有眼）只保留最多的搭子數，運算量固定。
 */

const SUIT_SIZE = 9;
const HONOR_START = 27;
const KIND_COUNT = 34;
const MAX_UNITS = 5; // 十六張最多五組面子／搭子
const GROUP_CACHE_LIMIT = 200_000; // 各組拆解快取上限（超過就不再新增）

/**
 * 一組牌的拆法摘要：索引 m * 2 + p（面子數 m、是否留眼 p）→ 最多能有幾個搭子，-1 表示拆不出。
 * 搭子越多向聽數只會越少，所以同一個 (m, p) 只需保留最多的搭子數，結果與窮舉全部組合相同。
 */
type TGroupStates = Int8Array;
const STATE_SLOTS = (MAX_UNITS + 1) * 2;
const COUNT_BASE = 5; // 每種牌 0～4 張，以 5 進位把一組計數編成整數 key

const suitCache = new Map<number, TGroupStates>();
const honorCache = new Map<number, TGroupStates>();

const decomposeGroup = (counts: number[], allowSequence: boolean): TGroupStates => {
  const states: TGroupStates = new Int8Array(STATE_SLOTS).fill(-1);
  const visit = (index: number, melds: number, partials: number, pair: number): void => {
    while (index < counts.length && counts[index] === 0) index++;
    if (index === counts.length) {
      const slot = melds * 2 + pair;
      if (partials > states[slot]!) states[slot] = partials;
      return;
    }
    const n = counts[index]!;
    if (n >= 3 && melds < MAX_UNITS) {
      counts[index]! -= 3;
      visit(index, melds + 1, partials, pair);
      counts[index]! += 3;
    }
    if (
      allowSequence &&
      index <= 6 &&
      counts[index + 1]! > 0 &&
      counts[index + 2]! > 0 &&
      melds < MAX_UNITS
    ) {
      counts[index]!--;
      counts[index + 1]!--;
      counts[index + 2]!--;
      visit(index, melds + 1, partials, pair);
      counts[index]!++;
      counts[index + 1]!++;
      counts[index + 2]!++;
    }
    if (n >= 2) {
      counts[index]! -= 2;
      if (!pair) visit(index, melds, partials, 1);
      if (partials < MAX_UNITS) visit(index, melds, partials + 1, pair);
      counts[index]! += 2;
    }
    if (allowSequence && partials < MAX_UNITS)
      for (const gap of [1, 2]) {
        if (index + gap < SUIT_SIZE && counts[index + gap]! > 0) {
          counts[index]!--;
          counts[index + gap]!--;
          visit(index, melds, partials + 1, pair);
          counts[index]!++;
          counts[index + gap]!++;
        }
      }
    counts[index]!--;
    visit(index, melds, partials, pair);
    counts[index]!++;
  };
  visit(0, 0, 0, 0);
  return states;
};

const groupStates = (counts: readonly number[], start: number, length: number): TGroupStates => {
  let key = 0;
  for (let i = start; i < start + length; i++) key = key * COUNT_BASE + counts[i]!;
  const isHonor = start === HONOR_START;
  const cache = isHonor ? honorCache : suitCache;
  const cached = cache.get(key);
  if (cached) return cached;
  const states = decomposeGroup(counts.slice(start, start + length), !isHonor);
  if (cache.size < GROUP_CACHE_LIMIT) cache.set(key, states);
  return states;
};

const GROUPS: readonly (readonly [number, number])[] = [
  [0, SUIT_SIZE],
  [SUIT_SIZE, SUIT_SIZE],
  [SUIT_SIZE * 2, SUIT_SIZE],
  [HONOR_START, KIND_COUNT - HONOR_START],
];

export class CHandAnalysis {
  private readonly counts = Array<number>(KIND_COUNT).fill(0);
  private readonly combined = new Int8Array(STATE_SLOTS);
  private readonly next = new Int8Array(STATE_SLOTS);
  shanten(kinds: readonly number[], fixedMelds: number): number {
    const counts = this.counts;
    counts.fill(0);
    for (const k of kinds) if (k < KIND_COUNT) counts[k]!++;
    const required = 5 - fixedMelds;
    // 合併各組：同一個 (m, p) 只留最多搭子數；m ≤ required、搭子截到 required、眼最多一個
    let combined = this.combined;
    let next = this.next;
    combined.fill(-1);
    combined[0] = 0;
    for (const [start, length] of GROUPS) {
      const states = groupStates(counts, start, length);
      next.fill(-1);
      for (let a = 0; a < STATE_SLOTS; a++) {
        const at = combined[a]!;
        if (at < 0) continue;
        const am = a >> 1;
        const ap = a & 1;
        for (let b = 0; b < STATE_SLOTS; b++) {
          const bt = states[b]!;
          if (bt < 0) continue;
          const bp = b & 1;
          if (ap + bp > 1) continue;
          const m = am + (b >> 1);
          if (m > required) continue;
          const slot = m * 2 + ap + bp;
          const t = Math.min(required, at + bt);
          if (t > next[slot]!) next[slot] = t;
        }
      }
      [combined, next] = [next, combined];
    }
    let best = required * 2;
    for (let slot = 0; slot < STATE_SLOTS; slot++) {
      const t = combined[slot]!;
      if (t < 0) continue;
      const m = slot >> 1;
      best = Math.min(best, required * 2 - m * 2 - Math.min(t, required - m) - (slot & 1));
    }
    return best;
  }
}
